import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader } from 'components/Shared/Loader'
import { useErrorHandling } from 'context/errorHandling'
import { fetchAllUsers } from 'services'
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete'
import { ReactComponent as DropdownIcon } from 'assets/icons/dropdown.svg'
import TextField from '@material-ui/core/TextField'
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg'
import withStyles from '@material-ui/core/styles/withStyles'
import { Chip as MemberChip } from 'components/Shared/Chip'
import styles from './SelectUsersField.module.css'
import { PrimaryButton } from 'components/Shared/PrimaryButton'
import SelectMembersDialog from './SelectMembersDialog'

export function SelectUsersField ({ members, onMembersChanged }) {
  return <>
    <MemberSelect members={members}
                  onMembersChanged={onMembersChanged}/>
    <MemberChips members={members}
                 onRemove={memberToRemove => onMembersChanged(members.filter(member => member !== memberToRemove))}
    />
  </>
}

const matchesInputFilter = createFilterOptions()

function MemberSelect ({ members, onMembersChanged }) {
  // clears input after value select
  const [rerenderTrigger, setRerenderTrigger] = useState(true)
  const [isSelectMembersDialogOpen, setSelectMembersDialogOpen] = useState(false)

  const [users, setUsers] = useState()

  const { handleError } = useErrorHandling()
  const { t } = useTranslation()

  useEffect(() => {
      fetchAllUsers()
        .then(setUsers)
        .catch(handleError)
    }, [handleError]
  )

  const mapUserToMember = (selectedUser) => ({
    userId: selectedUser.id,
    firstName: selectedUser.firstName,
    lastName: selectedUser.lastName,
    email: selectedUser.email,
    addedAt: selectedUser.creationDateTime
  })

  const onSelectMembersDialogClose = (selectedIds) => {
    if (selectedIds) {
      const selectedMembers = users.filter(user => selectedIds.includes(user.id)).map(mapUserToMember)
      onMembersChanged(selectedMembers)
    }
    setSelectMembersDialogOpen(false)
  }

  const filterOptions = (options, input) => {
    const selectedMembersIds = members.map(member => member.userId)
    return matchesInputFilter(options, input).filter(option => !selectedMembersIds.includes(option.id))
  }

  return users ?
    <div className={styles.selectContainer}>
      <SelectMembersDialog isOpen={isSelectMembersDialogOpen}
                           members={members}
                           onClose={onSelectMembersDialogClose}/>
      <StyledAutocomplete
        key={rerenderTrigger}
        options={users}
        getOptionLabel={user => `${user.firstName} ${user.lastName}${user.email ? (' (' + user.email + ')') : ''}`}
        onChange={(_, selectedUser) => {
          onMembersChanged([...members, mapUserToMember(selectedUser)])
          setRerenderTrigger(!rerenderTrigger)
        }}
        filterOptions={filterOptions}
        noOptionsText={t('form.select.noOptionsText')}
        popupIcon={<DropdownIcon/>}
        renderInput={params => (
          <TextField
            {...params}
            variant="filled"
            placeholder={t('form.search')}
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <SearchIcon/>
              ),
              disableUnderline: true
            }}
          />
        )}
      />
      <PrimaryButton
        className={styles.selectFromListButton}
        onClick={(event) => {
          event.preventDefault()
          setSelectMembersDialogOpen(true)
        }}
        label={'form.selectUsers.buttonTitle'}
      />
    </div> : <Loader/>
}

const StyledAutocomplete = withStyles(() => ({
  root: {
    width: '630px',
    minWidth: '310px',
    margin: '0 8px 8px 0',
    '& div.MuiAutocomplete-inputRoot': {
      paddingTop: 0,
      height: '50px'
    }
  }
}))(Autocomplete)

function MemberChips ({ members, onRemove }) {
  return <div className={styles.chipsContainer}>
    {members?.map(member =>
      <MemberChip
        key={member.email}
        onDelete={() => onRemove(member)}
        label={member.email ? member.email : `${member.firstName} ${member.lastName}`}
      />
    )}
  </div>
}